import { Directive, ElementRef, forwardRef, HostBinding, HostListener, inject, Input } from '@angular/core';

import { KH_A11Y_TARGET } from '../constants/a11y-target.token';
import { A11yBooleanInput, A11yInteractive } from '../interfaces/a11y.interfaces';
import { A11yKeyboardService } from '../services/a11y-keyboard.service';
import { isNativeFocusableControl, toBoolean } from '../utils/a11y.util';

import { A11yLabelDirective } from './a11y-label.directive';

@Directive({
  selector: '[khA11yClickable]',
  providers: [{ provide: KH_A11Y_TARGET, useExisting: forwardRef(() => A11yClickableDirective) }]
})
export class A11yClickableDirective extends A11yLabelDirective implements A11yInteractive {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly keyboardService = inject(A11yKeyboardService);

  @Input('khA11yClickable') public disabled: A11yBooleanInput = false;

  @HostBinding('attr.role')
  public get role(): 'button' | null {
    return this.isNativeControl ? null : 'button';
  }

  @HostBinding('attr.tabindex')
  public get tabindex(): string | null {
    if (this.disabledState) {
      return '-1';
    }

    return this.isNativeControl ? null : '0';
  }

  @HostBinding('attr.aria-disabled')
  public get ariaDisabled(): 'true' | null {
    return this.disabledState ? 'true' : null;
  }

  @HostListener('keydown', ['$event'])
  public onA11yKeydown(event: KeyboardEvent): void {
    if (this.disabledState || this.isNativeControl) {
      return;
    }

    this.keyboardService.activateOnEnterOrSpace(event, () => {
      (event.currentTarget as HTMLElement).click();
    });
  }

  private get disabledState(): boolean {
    return toBoolean(this.disabled);
  }

  private get isNativeControl(): boolean {
    return isNativeFocusableControl(this.elementRef.nativeElement);
  }
}
